/* eslint-disable @typescript-eslint/no-explicit-any */

import 'katex/dist/katex.min.css';
import 'react-photo-view/dist/react-photo-view.css';

import { Badge, Tooltip } from '@mantine/core';
import { IconExternalLink, IconLink } from '@tabler/icons-react';
import cx from 'clsx';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { CodeProps } from 'react-markdown/lib/ast-to-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import RehypeHighlight from 'rehype-highlight';
import RehypeKatex from 'rehype-katex';
import RemarkBreaks from 'remark-breaks';
import RemarkGfm from 'remark-gfm';
import RemarkMath from 'remark-math';
// @ts-ignore
import LinkifyIt from 'linkify-it';
import rehypeRaw from 'rehype-raw';
import { MermaidBlock } from './MermaidBlock';
import { useChatStore } from '@mygpt/app/chat';
import { MessageTools } from '@mygpt/app/chat/components/MessageTools';
import {
  checkIsUrl,
  checkObjectType,
  comprehensiveUrlRegex,
  UrlProcessor,
  safeJSONParse,
  ThirdPartyScriptsManager,
} from '@mygpt/app/utils';
import { Footnote, ReferenceSign } from '@mygpt/types';

import classes from './markdown.module.css';
import { useFilePreviewStore } from '@mygpt/app/chat/stores/useFilePreviewStore';

const CodeComponent = ({ children, className, inline, node, ...props }: CodeProps) => {
  const match = /language-(\w+)/.exec(className || '');
  const [code, setCode] = useState('');
  const ref = useRef<HTMLPreElement>(null);
  const lang = match?.[1] != null ? match[1] : 'unknown';
  const isHtml = lang === 'html';
  const isCss = lang === 'css';
  const isJs = ['javascript', 'typescript', 'js', 'jsx', 'tsx'].includes(lang.toLowerCase());

  useLayoutEffect(() => {
    setCode(ref.current?.innerText ?? '');
  }, [children]);

  // 如果是 Mermaid 图表，使用专门的 Mermaid 组件
  if (lang === 'mermaid' && !inline) {
    return <MermaidBlock chart={String(children)} />;
  }

  // CodePen all options: https://blog.codepen.io/documentation/api/prefill/
  const data: Record<string, string> = {
    title: `Code from gbase.ai`,
  };
  if (isHtml) {
    data['html'] = code;
  } else if (isCss) {
    data['css'] = code;
  } else if (isJs) {
    data['js'] = code;
  }

  const isFrontEndCode = isHtml || isCss || isJs;

  return !inline ? (
    <>
      <div className="flex justify-between items-center text-gray-200 dark:text-gray-400 bg-dark-[#334155] px-4 py-2 text-xs font-sans rounded-t-md">
        <span>{lang !== 'unknown' && lang}</span>
        <div className="flex space-x-2 items-center">
          {isFrontEndCode && (
            <form action="https://codepen.io/pen/define" method="POST" target="_blank">
              <input type="hidden" name="data" value={JSON.stringify(data)} />
              <button type="submit">
                <IconExternalLink size={16} className="inline align-text-bottom" />
              </button>
            </form>
          )}

          <MessageTools content={() => code} className="text-gray-200 hover:text-white" />
        </div>
      </div>
      <div className="overflow-y-auto">
        <code className={cx(classes['code'], className)} ref={ref}>
          {children}
        </code>
      </div>
    </>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

const ImageComponent = ({
  alt,
  src,
}: React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>) => {
  let width: number | undefined = undefined;
  let height: number | undefined = undefined;
  const match = src?.match(/wh=([\d.]+)x([\d.]+)/);

  if (match) {
    width = parseInt(match[1]);
    height = parseInt(match[2]);
  }

  return (
    <PhotoView src={src}>
      <img src={src} alt={alt} width={width} height={height} style={{ cursor: 'pointer' }} />
    </PhotoView>
  );
};

const linkify = new LinkifyIt();

const extractLinks = (text: string): { url: string; text: string }[] => {
  const links = linkify.match(text);
  if (links) {
    return links.map((link: { url: string; text: string }) => {
      let url = link.url;
      let cleanedText = link.text;

      // linkify-it 会将URL后面的中文标点和文字也识别为URL的一部分
      // 需要从URL末尾移除这些非法字符
      // 匹配模式：从第一个CJK字符（中文标点、中日韩文字等）开始到字符串结尾的所有内容
      const invalidSuffixMatch = url.match(/([\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff\uff00-\uffef].*)$/);

      if (invalidSuffixMatch) {
        // 移除从第一个中文字符开始的所有后续内容
        const invalidSuffix = invalidSuffixMatch[0];
        url = url.substring(0, url.length - invalidSuffix.length);
        // 同时更新text
        if (cleanedText.endsWith(invalidSuffix)) {
          cleanedText = cleanedText.substring(0, cleanedText.length - invalidSuffix.length);
        }
      }

      // 额外处理：移除URL末尾的URL编码中文字符（如 %E4%B8%AD）
      // 但是要注意：如果URL包含query参数（?）或fragment（#），则编码的中文可能是合法的参数值
      // 只在没有query参数和fragment的情况下，才移除末尾的编码中文
      const hasQueryOrFragment = url.includes('?') || url.includes('#');
      if (!hasQueryOrFragment) {
        const encodedSuffixMatch = url.match(/(%[0-9A-Fa-f]{2})+$/);
        if (encodedSuffixMatch) {
          try {
            const decodedSuffix = decodeURIComponent(encodedSuffixMatch[0]);
            // 如果解码后包含CJK字符，说明这不应该是URL的一部分
            if (/[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff\uff00-\uffef]/.test(decodedSuffix)) {
              const suffixLength = encodedSuffixMatch[0].length;
              url = url.substring(0, url.length - suffixLength);
              if (cleanedText.endsWith(encodedSuffixMatch[0])) {
                cleanedText = cleanedText.substring(0, cleanedText.length - suffixLength);
              }
            }
          } catch (e) {
            // 解码失败，保持原URL
          }
        }
      }

      return {
        url: url,
        text: cleanedText,
      };
    });
  }
  return [];
};

const LinkComponent = ({
  children,
  href,
  ...props
}: React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>) => {
  const { pageSource, setShownPdfCanvas } = useChatStore();
  const isWidget = pageSource === 'widget';
  const { clickReference, referenceDataMap } = React.useContext(ReferenceContext);
  const { t } = useTranslation();

  if (href?.startsWith('#ref_') && referenceDataMap) {
    const refId = href.substring(1);
    const refData = referenceDataMap.get(refId);

    if (refData) {
      const isSourceHttpLink = refData.source?.startsWith('http://') || refData.source?.startsWith('https://');
      const isExcelFile = refData.source?.match(/\.(xlsx?|xls|xlsm|xlsb|csv)$/i);
      return (
        <Tooltip label={refData.source} withArrow>
          <span
            onClick={(e) => {
              e.preventDefault();
              if (isSourceHttpLink) {
                window.open(refData.source, '_blank', 'noopener,noreferrer');
              } else if (refData.file_id) {
                if (isExcelFile) {
                  const downloadUrl = `${window.location.protocol}//${window.location.host}/api/embeddings/files/${refData.file_id}/download`;
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = refData.source || 'file';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } else {
                  setShownPdfCanvas(refData);
                }
              } else if (clickReference) {
                clickReference(refData);
              }
            }}
            className="inline-flex items-center justify-center ml-[2px] mr-[2px] rounded w-5 h-5 bg-gray-100 text-gray-500 cursor-pointer transition-colors hover:bg-gray-200"
          >
            <IconLink size={12} />
          </span>
        </Tooltip>
      );
    }
  }

  // Handle old reference links (keeping for backward compatibility)
  if (href === '#reference' && (props as any)['data-file-id']) {
    const fileId = (props as any)['data-file-id'] as string;
    const page = parseInt((props as any)['data-page'] as string);
    const filename = (props as any)['data-filename'] as string;

    return (
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          if (clickReference) {
            clickReference({ file_id: fileId, page, filename });
          }
        }}
        style={{ color: '#7c3aed', textDecoration: 'underline', cursor: 'pointer' }}
      >
        {children}
      </a>
    );
  }

  // 检查是否是邮箱地址
  const isEmail = href?.startsWith('mailto:');
  // 检查是否是电话号码
  const isTel = href?.startsWith('tel:');

  if (isEmail || isTel) {
    return (
      <a href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  // 使用 extractLinks 提取链接，并将文本拆分为链接和普通文本
  const childrenText = children?.toString() || '';
  const links = extractLinks(childrenText);

  // 如果提取到链接，需要将原始文本拆分为链接和普通文本部分
  if (links.length > 0) {
    const components: React.ReactNode[] = [];
    let lastIndex = 0;

    links.forEach((link, index) => {
      // 在原始文本中找到这个链接的位置
      const linkIndex = childrenText.indexOf(link.text, lastIndex);

      if (linkIndex > lastIndex) {
        // 添加链接前的普通文本
        components.push(childrenText.substring(lastIndex, linkIndex));
      }

      // 添加可点击的链接
      components.push(
        <a key={index} href={link.url} target="_blank" rel="noreferrer">
          {link.text}
        </a>
      );

      lastIndex = linkIndex + link.text.length;
    });

    // 添加最后一个链接后面的普通文本
    if (lastIndex < childrenText.length) {
      components.push(childrenText.substring(lastIndex));
    }

    return <>{components}</>;
  }

  let width: number | undefined = undefined;
  let height: number | undefined = undefined;
  const match = href?.match(/wh=([\d.]+)x([\d.]+)/);
  // 兼容解析图片导致感叹号变成中文了的问题（会变成链接了）
  if (match) {
    width = parseInt(match[1]);
    height = parseInt(match[2]);

    return <img src={href} alt={children?.toString()} width={width} height={height} />;
  }

  // 处理链接和文字粘在一起的问题
  const childrenContent = children ? (children as Array<string>)[0] : '';
  if (children && checkObjectType(children) === 'Array' && childrenContent !== href) {
    const urlMatch =
      childrenContent.length > 200
        ? /https?:\/\/[-a-zA-Z0-9@:%._+~#=]{2,256}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g
        : new RegExp(comprehensiveUrlRegex, 'gi');
    let matchResult = urlMatch.exec(childrenContent);
    let componentArr: { type: 'text' | 'link'; content: string; href?: string }[] = [];

    for (; matchResult !== null; ) {
      // 匹配不上或者完全匹配只有url就跳过
      if (!matchResult || matchResult[0] === matchResult.input) break;
      const [url, urlProtocolMatched] = matchResult;
      const startStr = matchResult.input.substring(0, matchResult.index);
      const urlProcessor = new UrlProcessor(url, urlProtocolMatched);
      const endStr = matchResult.input.substring(matchResult.index + matchResult[0].length);

      matchResult = urlMatch.exec(endStr);

      componentArr = [
        ...componentArr,
        { type: 'text', content: startStr },
        { type: 'link', content: urlProcessor.getAnchorText(), href: urlProcessor.getAnchorHref() },
        { type: 'text', content: matchResult ? '' : endStr },
      ];
    }

    if (componentArr.length) {
      return (
        <>
          {componentArr.map((item, index) => {
            if (item.type === 'link') {
              return (
                <a key={index} href={item.href} target="_blank" rel="noreferrer">
                  {item.content}
                </a>
              );
            }
            return item.content;
          })}
        </>
      );
    }
  }

  const url = href && checkIsUrl(href) ? new URL(href) : '';
  if (isWidget && url) {
    const searchParams = url.searchParams;

    if (!searchParams.has('fg')) {
      if (searchParams.toString()) {
        // 如果已有其他查询参数，则添加&fg=1
        searchParams.append('fg', '1');
      } else {
        // 如果没有任何查询参数，则添加?fg=1
        searchParams.set('fg', '1');
      }
    }
    // 更新URL
    url.search = searchParams.toString();
  }

  return (
    <a
      href={url.toString()}
      target="_blank"
      rel="noreferrer"
      onClick={() => {
        isWidget && ThirdPartyScriptsManager.safeMixpanelTrack('link_clicked', { link: href });
      }}
    >
      {children ? children : href}
    </a>
  );
};

interface InlineCitationProps {
  // 当前引用对应的文件ID
  file?: string;
  // PDF 引用特有字段
  page?: string | number;
  // TableRAG 引用特有字段
  filename?: string;
  // TableRAG 引用特有字段
  // "[2, 4, 8]"
  rows?: string;
  // TableRAG 引用特有字段
  sheet?: string;
  // 网页引用的URL
  url?: string;
  node: {
    position?: {
      start?: {
        offset?: number;
        // TableRAG 引用特有字段
        line?: number;
        // TableRAG 引用特有字段
        column?: number;
      };
    };
  };
  rawContent: string;
  children?: React.ReactNode;
  reference?: Footnote;
}

const parseCitationPage = (page?: string | number): number => {
  if (page === undefined || page === null) return 0;

  const normalizedPage = String(page);
  const firstPage = normalizedPage.split('-')[0]?.trim();
  const parsedPage = Number(firstPage);

  if (Number.isNaN(parsedPage)) return 0;
  return Math.max(0, parsedPage);
};

// 回答中的引用弹窗的内容组件
// 每次鼠标悬浮展示内容时，
const ReferenceLabelContent = ({
  file,
  title,
  validPage,
  citationOrder,
  content,
  url,
}: {
  file?: string;
  title: string;
  validPage: number;
  citationOrder: number;
  content?: string;
  url?: string;
}) => {
  const { pageMap } = useFilePreviewStore();

  // 如果是网页引用（有URL），只展示标题
  if (url) {
    return title;
  }

  // 先尝试获取更准确的页码
  const realPage = content ? pageMap[`${citationOrder}${file}${content}`] : undefined;

  if (realPage === -1) {
    // 表示接口返回了，但没有高亮位置数据
    // 那么这里只展示标题
    return title;
  }

  // 只有第二次高亮接口返回并且有有效数据时才展示页码。
  if (realPage !== undefined) {
    return `${title} p${realPage + 1}`;
  }

  // 第一次回答的时候，不展示页码，因为准确率太低。
  return title;
};

/**
 * 渲染单个引用标记，并在点击时回传引用前的上下文 Markdown 片段，方便外部处理高亮或跳转。
 * page: 后端返回的，从0开始计数
 * url: 网页引用的URL，如果有URL则在新标签页打开
 */
const InlineReferenceComponent = ({
  file,
  page,
  url,
  filename,
  rows,
  sheet,
  node,
  rawContent,
  reference,
}: InlineCitationProps) => {
  const { setReferenceInfo, setExcelReferenceInfo } = useFilePreviewStore();
  const { pageSource } = useChatStore();
  const isWidget = pageSource === 'widget';

  console.log('渲染引用组件', { file, page, filename, rows, sheet, node, rawContent, reference });

  const citationOrder = useMemo(() => {
    const offset = node?.position?.start?.offset ?? -1;

    if (!rawContent || offset < 0) return 1;
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return 1;

    const beforeText = rawContent.slice(0, offset);
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(beforeText, 'text/html');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    const orderedNodes: Node[] = [];
    let current = walker.nextNode();

    while (current) {
      orderedNodes.push(current);
      current = walker.nextNode();
    }

    let count = 0;

    for (let i = orderedNodes.length - 1; i >= 0; i--) {
      const currentNode = orderedNodes[i];

      if (currentNode.nodeType === Node.TEXT_NODE) {
        const text = currentNode.textContent ?? '';
        if (text.trim() === '') {
          continue;
        }
        break;
      }

      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const elementNode = currentNode as Element;
        if (elementNode.tagName.toLowerCase() === 'citation') {
          count += 1;
          continue;
        }
        break;
      }
    }

    return count + 1;
  }, [node, rawContent]);

  /**
   * 从上一个引用开始裁剪到当前引用(或者到文本开头)位置，用于还原引用前的上下文内容。
   * 但要注意：可能前面会紧跟着另外N个 citation 标签文本，应该排除掉它们
   */
  const buildSnippet = (fullText: string) => {
    const offset = node?.position?.start?.offset ?? -1;

    if (offset < 0) return '';
    const beforeText = fullText.slice(0, offset); // 当前引用之前的原文

    // 使用 split 分割文本，找到最后一个引用标签前的文本内容
    const parts = beforeText.split(/<citation.*?\/citation>/gi);
    const trimParts = parts.filter((text) => text.trim());

    return trimParts[trimParts.length - 1];
  };

  // 支持单页 `16` 和范围 `15-17`，点击时统一使用范围起始页。
  const validPage = parseCitationPage(page);

  /**
   * 将引用信息与切好的上下文片段传给外部处理函数。
   * 如果是网页引用（有url），则在新标签页打开URL。
   * url来源优先级：1. CITATION标签的url属性  2. reference对象的url字段
   */

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();

    // 如果是网页引用，在新标签页打开URL
    // 优先使用标签中的url属性，其次使用reference对象的url字段
    const webUrl = url || reference?.url;
    if (webUrl) {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!file) return;

    // 如果根据文件名后缀判断是Excel文件
    const isExcel = ['.xlsx', '.xls', '.xlsm', '.csv'].some((suffix) => reference?.title.endsWith(suffix));
    if (isExcel) {
      const parsedRows = (safeJSONParse(rows) as number[]) || [];
      setExcelReferenceInfo({
        file_id: file,
        rows: parsedRows, // tableRAG引用特有
        sheet: sheet || '',
        title: reference?.title,
      });
    } else {
      setReferenceInfo({
        file_id: file,
        page: validPage, // 从0开始计数
        content: buildSnippet(rawContent || ''),
        title: reference?.title,
        citationOrder: citationOrder,
      });
    }

    const source = reference?.source!;
    const labelText = `${reference?.title || source} p${Number(page)}`;

    if (checkIsUrl(source, true)) {
      isWidget &&
        ThirdPartyScriptsManager.safeMixpanelTrack('reference_content_clicked', {
          source,
          title: labelText,
        });
    } else {
      if (file && page) {
        //统计
        isWidget &&
          ThirdPartyScriptsManager.safeMixpanelTrack('reference_content_clicked', {
            source,
            page_numbers: Number(page),
            file_id: file,
            title: labelText,
          });
      }
    }
  };

  // 如果没有 title，就不要 Tooltip
  // catalog-agent 的引用没有返回 title
  if (!reference?.title.trim()) {
    return (
      <Badge color="#ECEDEB" size="xs" className="cursor-pointer mr-1" onClick={handleClick}>
        <span className="text-[#626266]">{citationOrder}</span>
      </Badge>
    );
  }

  // 展示给用户看的 px 应该 + 1
  return (
    <Tooltip
      label={
        <ReferenceLabelContent
          file={file}
          title={reference?.title || ''}
          validPage={validPage}
          content={buildSnippet(rawContent || '')}
          citationOrder={citationOrder}
          url={url || reference?.url}
        />
      }
    >
      <Badge color="#ECEDEB" size="xs" className="cursor-pointer mr-1" onClick={handleClick}>
        <span className="text-[#626266]">{citationOrder}</span>
      </Badge>
    </Tooltip>
  );
};

// 后端可能会返回错误的引用标签，前端统一兜底过滤掉无效标签，避免渲染出无法点击预览的伪角标。
// 一条 <CITATION ... /> 当且仅当满足下列条件之一才会保留：
// 1. 有 file 属性且 file_id 命中 referenceList
// 2. 有 url 属性且为 http(s):// 开头；referenceList 非空时还需命中其中某个 url（catalog-agent 等
//    工具调用路径 referenceList 可能为空，此时仅依赖 http(s) 前缀判定，与原行为兼容）
// 既没有 file 也没有 url 的 CITATION 一律丢弃，覆盖 FAQ RAG 路径下 LLM 幻觉出的 source/detail 形态、
// 以及外部 CITATION_INSTRUCTIONS 强制生成的 <CITATION url="Internal Knowledge" /> 等无效标签。
const removeWrongInlineReferenceTag = (content: string, referenceList?: Footnote[]): string => {
  const hasReferenceList = !!(referenceList && referenceList.length);
  const validFileIds = new Set(hasReferenceList ? referenceList!.map((ref) => ref.file_id) : []);
  const validUrls = new Set(hasReferenceList ? referenceList!.filter((ref) => ref.url).map((ref) => ref.url) : []);

  return content.replace(/<CITATION\b[^>]*\/>/g, (match) => {
    const fileMatch = match.match(/file="([^"]+)"/);
    const urlMatch = match.match(/url="([^"]+)"/);

    if (urlMatch) {
      const url = urlMatch[1].trim();
      const lower = url.toLowerCase();
      if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
        return '';
      }
      // catalog-agent / 工具调用路径 referenceList 可能为空，此时仅依赖 http(s) 前缀豁免
      if (!hasReferenceList) return match;
      return validUrls.has(url) ? match : '';
    }

    if (fileMatch) {
      if (!hasReferenceList) return '';
      return validFileIds.has(fileMatch[1]) ? match : '';
    }

    // 既无 file 也无 url：一律视为无效引用
    return '';
  });
};

// 正则，将目前为止的整个完整文本消息中的引用标记，替换���ReactMarkdown组件可以处理的内容
// 未闭合标签 -> 合法的闭合标签
// <citation file="d4b4" page="4"> -> <citation file="d4b4" page="4"></citation>
// <CITATION file="22920c54-31b2-4bdc-9902-a16fc9fe3a53" filename="students_list.xlsx" sheet=1 rows=[2, 3, 4, 5, 6, 7, 8, 9] /> -> <citation file="22920c54-31b2-4bdc-9902-a16fc9fe3a53" filename="students_list.xlsx" sheet="1" rows="[2, 3, 4, 5, 6, 7, 8, 9]" ></citation>
const replaceInlineReferenceTag = (content: string) =>
  // 同时将标签前面的：空白符号 去除，这样本来换行展示的引用元素就可以inline展示了。
  content.replace(/[\s]*<CITATION\b([^>]*?)(?:\/)?>/g, (_match, attrs = '') => {
    // 给没有引号的属性值加上引号
    // 匹配：name=value 格式，其中 value 不以引号开头
    // 匹配直到下一个类似 " name=" 的模式或字符串末尾
    const quotedAttrs = attrs.replace(/(\S+)=([^\s"'][^]*?)(?=\s+\S+=|$)/g, (_attrMatch, name, value) => {
      // 去除 value 末尾可能的空白
      const trimmedValue = value.trim();
      return `${name}="${trimmedValue}"`;
    });
    return `<citation${quotedAttrs ? ` ${quotedAttrs}` : ''} ></citation>`;
  });

// 可能LLM会将输出的标签放在``符号里，导致页面上不会渲染它。
// 针对这种情况，正则替换掉``符号。
// `<citation file="d4b4" page="4"></citation>`
// `<citation file="22920c54-31b2-4bdc-9902-a16fc9fe3a53" filename="students_list.xlsx" sheet="1" rows="[2, 3, 4, 5, 6, 7, 8, 9]" ></citation>`
const replaceMarkdownCodeReferenceTag = (content: string) =>
  content.replace(/`(<citation\b[^>]*><\/citation>)`/g, '$1');

// 删除不完整的标签（当前主要有引用标签），防止不完整的的标签出现在答案中
// 完整的标签不会处理
// 完整标签：
// 1. <CITATION file="24fe8dd4-9e03-4183-b9d4-ea9280d2033c" page="26">
// 2. <CITATION file="22920c54-31b2-4bdc-9902-a16fc9fe3a53" filename="students_list.xlsx" sheet=1 rows=[2, 3, 4, 5, 6, 7, 8, 9] />
// 流式传输的答案数据中会传输不完整的标签，防止出现在页面中这种标签，所以就先删除掉。
const removeIncompleteReferenceTag = (content: string) => {
  return content.replace(/<[^>]*$/g, '');
};

// LLM可能返回和约定的引用标签完全不一样的内容，在前端兜底，过滤掉
// 比如：<file_id="778cff5b-0d6b-4ba1-ba05-8697ce9page=1/>
const removeInvalidTags = (content: string): string => {
  return content.replace(/<\s*\/?file_id[^>]*\/?>/gi, '');
};

// 删除多余的引用标签，因为LLM可能幻觉，导致连续输出几十个引用
// 所以在前端兜底，最多展示 count 个（只处理连续的标签，如果标签中间有其他文字，没有这个限制）
const removeExtraReferenceTag = (content: string, count: number = 3) => {
  // 构建动态正则，匹配超过 count 个的连续引用标签组
  const minCount = count + 1;
  const continuousCitationRegex = new RegExp(`((?:<CITATION\\b[^>]*>\\s*){${minCount},})`, 'gi');

  return content.replace(continuousCitationRegex, (match) => {
    // 提取这组中的所有引用标签
    const citationRegex = /<CITATION\b[^>]*>/gi;
    const citations = match.match(citationRegex) || [];

    // 保留前 count 个引用和它们之间的空白
    const result = citations.slice(0, count).join('');
    return result;
  });
};

type GetReference = () => Promise<ReferenceSign[]>;
const ReferenceContext = React.createContext<{
  getReference?: () => Promise<ReferenceSign[]>;
  referenceDataMap?: Map<string, any>;
}>({} as any);

export function Markdown(props: {
  content: string;
  className?: string;
  getReference?: GetReference;
  isDigitalHuman?: boolean;
  referenceList?: Footnote[];
  botMessageTextColor?: string;
  suffixNode?: React.ReactNode;
}) {
  const { getReference, referenceList, suffixNode } = props;
  const referenceDataMap = useRef<Map<string, any>>(new Map());

  let content = props.content;

  // 兼容解析图片导致感叹号变成中文了的问题（会变成链接了）
  // 修复：使用排除字符类防止多个连续图片被合并
  content = content?.replace(/(!|！)(\[|【)([^\]】]*?)(\]|】)(\(|（)([^\)）]*?)(\)|）)/g, (...args) => {
    return `![${args[3].replace(/\n|\r\n/g, '')}](${args[6].replace(/\n|\r\n/g, '')})`;
  });
  // 修复：在连续的图片之间添加换行符，确保ReactMarkdown能正确解析
  // 只匹配 ![...](...)![...] 这种连续图片的模式，避免误伤其他markdown元素
  content = content?.replace(/(\!\[[^\]]*\]\([^\)]*\))(\!\[)/g, '$1\n\n$2');
  // 兼容解析文本链接（https://www.xxx.com）adfdf 全角括号或者括号后面没有空格导致把括号后面的内容也做成一部分了
  // 使用精确的 URL 字符集匹配，而不是排除法，避免将中文标点和文字误认为 URL 的一部分
  content = content?.replace(
    /([(（])(https?(:|\\*:)\/\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+)([)）])/g,
    (...args) => `(<${args[2].replace(/\\*:\/\//g, '://')}>)`
  );

  content = content?.replace(/<br \/>/g, '\n\n');
  content = content?.replace(/\\n/g, '\n');
  content = content?.replace(/\\r/g, '');

  content = removeInvalidTags(content);
  content = removeExtraReferenceTag(removeIncompleteReferenceTag(content), 6);
  content = removeWrongInlineReferenceTag(content, referenceList);
  content = replaceInlineReferenceTag(content);
  content = replaceMarkdownCodeReferenceTag(content);
  // 如果引用标签跟在了竖线后面，会被当成表格Markdown的一部分，导致解析异常，不能正常显示引用标签。替换成展示在下一行。
  content = content.replace('|<citation', '|\n<citation');

  return (
    <ReferenceContext.Provider value={{ getReference, referenceDataMap: referenceDataMap.current }}>
      <section
        className={cx(
          'prose dark:prose-invert',
          props.isDigitalHuman && `text-white ${classes['digitalHuman']}`,
          classes['highlightContainer'],
          classes['fullWidth'],
          suffixNode && classes['streaming'],
          props.className
        )}
        style={{
          wordBreak: 'break-word',
          ...(props.botMessageTextColor &&
            ({
              color: props.botMessageTextColor,
              '--tw-prose-body': props.botMessageTextColor,
              '--tw-prose-headings': props.botMessageTextColor,
              '--tw-prose-bold': props.botMessageTextColor,
              '--tw-prose-links': props.botMessageTextColor,
              '--tw-prose-code': props.botMessageTextColor,
              '--tw-prose-quotes': props.botMessageTextColor,
            } as React.CSSProperties)),
        }}
      >
        <PhotoProvider maskOpacity={0.6}>
          <ReactMarkdown
            remarkPlugins={[RemarkMath, RemarkGfm, RemarkBreaks]}
            rehypePlugins={[
              rehypeRaw,
              RehypeKatex,
              [
                RehypeHighlight,
                {
                  detect: false,
                  ignoreMissing: true,
                },
              ],
            ]}
            skipHtml={false}
            components={{
              code: CodeComponent,
              img: ImageComponent,
              a: LinkComponent,
              h1: 'h3',
              h2: 'h3',
              citation: (props: InlineCitationProps) => (
                <InlineReferenceComponent
                  {...props}
                  rawContent={content || ''}
                  reference={referenceList?.find(
                    (item) => (props.file && item.file_id === props.file) || (props.url && item.url === props.url)
                  )}
                />
              ),
            }}
            linkTarget={'_blank'}
          >
            {content}
          </ReactMarkdown>
          {suffixNode}
        </PhotoProvider>
      </section>
    </ReferenceContext.Provider>
  );
}
